import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import i18n from "micro-i18n";
import { findInArrayByKeyValue, isArray, isNullOrUndefOrEmpty, isString, toObject } from "utils";
import { DelayTimer, KeyboardEventKeyCode } from "consts";
import { IconClose } from "components/icons/Icons";
import classNames from "classnames";

//FIXME: SOF-617 requires the following imports to be moved to components/widget-components.scss
// due to lazy loading of the widget inside an iframe. Keeping this here for potential fix down the road.
// import 'components/questions/AutoComplete.scss';

const { Enter, BackSpace, Delete, DownArrow, RightArrow } = KeyboardEventKeyCode;

/**
 *
 * @param className {string}
 * @param placeholder {string}
 * @param options {[{id: number|string, text: string}]}
 * @param initialValue {string}
 * @param onSelectChoice {function}
 * @returns {JSX.Element}
 * @constructor
 *
 * Example: <AutoComplete placeholder="Enter Fruit" options={[{id: 1, text: 'Apple'},{id: 2, text: 'Pear'}]} />
 */
const AutoComplete = ({
  className,
  placeholder = i18n.t("AUTOCOMPLETE_YOUR_ANSWER"),
  options = [],
  initialValue = "",
  onSelectChoice,
}) => {
  const inputRef = useRef(null);
  const clearBtnRef = useRef(null);
  const activeOptionRef = useRef(null);
  const [currentSuggestions, setCurrentSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(initialValue);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const hasOptions = isArray(options) && options.length > 0;
  const hasSuggestions = isArray(currentSuggestions) && currentSuggestions.length > 0;
  const hasClearableSelection = !isNullOrUndefOrEmpty(selectedOptionId) && !isNullOrUndefOrEmpty(inputValue);

  const autoCompleteSuggestionsClassName = `csw-autocomplete-results ${isOpen ? "expand" : "collapse"}`;
  const autoCompleteSuggestionsStyle = { display: isOpen ? "block" : "none" };

  const getOptionFromText = (textToFind) =>
    options.find((option) => {
      const { text: optionText } = option;
      return isString(optionText) && isString(textToFind) && optionText.toLowerCase() === textToFind.toLowerCase();
    });
  const getOptionFromId = (idToFind) => findInArrayByKeyValue(options, "id", idToFind);

  const initCurrentSuggestions = (fromOptions, iValue) => {
    setCurrentSuggestions(
      fromOptions.filter((option) => {
        const { text } = option;
        return text.toLowerCase().includes(iValue ? iValue.toLowerCase() : null);
      }),
    );
  };

  // componentDidMount options, inputValue
  useEffect(() => {
    hasOptions && inputValue && initCurrentSuggestions(options, inputValue);
  }, [hasOptions, options, inputValue]);

  // componentDidMount activeOptionRef, selectedOptionId
  useEffect(() => {
    selectedOptionId &&
      activeOptionRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
  }, [activeOptionRef, selectedOptionId]);

  const focusInputRef = () => {
    setTimeout(() => {
      inputRef && inputRef.current && inputRef.current?.focus();
    }, DelayTimer.AutoCompleteDelay);
  };

  const handleInputEvent = (e) => {
    const { target } = e;
    const updatedInputValue = target.value;
    setInputValue(updatedInputValue);
    if (updatedInputValue.length > 0) {
      hasClearableSelection && clearBtnRef && clearBtnRef.current && clearBtnRef.current?.focus();
      setIsOpen(!hasClearableSelection);
    } else {
      setSelectedOptionId(null);
      onSelectChoice(null);
    }
  };

  const handleFocusEvent = (e, openResults = false) => {
    return setTimeout(() => {
      inputRef && inputRef.current && setIsOpen(openResults);
    }, DelayTimer.AutoCompleteDelay);
  };

  const onSuggestionClick = (e) => {
    const { target } = e;
    const updatedInputValue = target.firstChild.data;
    const updatedSelectedSuggestion = getOptionFromText(updatedInputValue);
    setInputValue(updatedInputValue);
    if (!isNullOrUndefOrEmpty(updatedSelectedSuggestion)) {
      setSelectedOptionId(updatedSelectedSuggestion.id);
      onSelectChoice(updatedSelectedSuggestion);
      focusInputRef();
    }
  };

  const clearSuggestion = () => {
    setSelectedOptionId(null);
    setInputValue(null);
    onSelectChoice(null);
    setIsOpen(true);
    hasOptions && initCurrentSuggestions(options, null);
    focusInputRef();
  };

  const handleKeyUp = (e) => {
    if (!isOpen) {
      return;
    }
    const selectedOptionFromId = getOptionFromId(selectedOptionId);
    const selectedOptionFromInputValue = getOptionFromText(inputValue);
    const currentSelectedOption = toObject(selectedOptionFromId || selectedOptionFromInputValue);
    const { id: currentSelectedId, text: currentSelectedText } = currentSelectedOption;
    const currentSelectedIndex = options.indexOf(currentSelectedOption);
    const { keyCode } = e;

    const suggestionArray = hasSuggestions ? currentSuggestions : options;

    const pickNextSelection = (nextSelectionIndex) => {
      const nextSelectedChoice = suggestionArray[nextSelectionIndex];
      const { id: nextSelectedChoiceId } = toObject(nextSelectedChoice);
      setSelectedOptionId(nextSelectedChoiceId);
      setIsOpen(true);
    };

    switch (keyCode) {
      case Enter: {
        setInputValue(currentSelectedText);
        setSelectedOptionId(currentSelectedId);
        onSelectChoice(currentSelectedOption);
        !isNullOrUndefOrEmpty(currentSelectedId) && setIsOpen(false);
        break;
      }
      case DownArrow: {
        const downSelectionIndex = currentSelectedIndex + 1;
        const nextSelectionIndex = downSelectionIndex >= suggestionArray.length ? 0 : downSelectionIndex;
        pickNextSelection(nextSelectionIndex);
        break;
      }
      case RightArrow: {
        const rightSelection = currentSelectedIndex - 1;
        const nextSelectionIndex = rightSelection < 0 ? suggestionArray.length - 1 : rightSelection;
        pickNextSelection(nextSelectionIndex);
        break;
      }
      case Delete: {
        clearSuggestion();
        break;
      }
    }
  };

  const handleKeyDown = (e) => {
    const { keyCode } = e;

    const keyDownClear = () => {
      if (hasClearableSelection) {
        e.preventDefault();
        clearSuggestion();
      }
    };

    switch (keyCode) {
      case BackSpace: {
        keyDownClear();
        break;
      }
      case Delete: {
        keyDownClear();
        break;
      }
    }
  };

  const buildSuggestionOptions = () => {
    const optionsToMap = hasSuggestions ? currentSuggestions : options;
    return optionsToMap.map((suggestion, index) => {
      const { text, id } = suggestion;
      const { id: selectedId } = toObject(getOptionFromId(selectedOptionId));
      const isActiveOption = id === selectedId;
      const mergedResultClassName = `csw-autocomplete-result ${isActiveOption ? "active" : ""}`;
      return (
        <button
          key={`csw-auto-complete-option-${index}`}
          type="button"
          onClick={onSuggestionClick}
          className={mergedResultClassName}
          ref={isActiveOption ? activeOptionRef : null}
        >
          {text}
        </button>
      );
    });
  };

  return (
    <div
      className={classNames("csw-autocomplete", className, {
        "with-suggestion": hasClearableSelection,
      })}
    >
      <input
        value={inputValue}
        className="csw-select outline csw-autocomplete-text-input"
        type="text"
        autoComplete="off"
        onInput={handleInputEvent}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyDown}
        onFocus={(e) => handleFocusEvent(e, !hasClearableSelection)}
        onBlur={(e) => handleFocusEvent(e, false)}
        placeholder={placeholder}
        ref={inputRef}
        readOnly={hasClearableSelection}
      />
      {hasClearableSelection && (
        <button
          type="button"
          onClick={clearSuggestion}
          onFocus={(e) => handleFocusEvent(e, true)}
          className="csw-btn csw-autocomplete-clear-btn outline"
          ref={clearBtnRef}
          title={i18n.t("AUTOCOMPLETE_CLEAR_YOUR_ANSWER")}
          aria-label={i18n.t("AUTOCOMPLETE_CLEAR_YOUR_ANSWER")}
        >
          <IconClose />
        </button>
      )}
      <div className={autoCompleteSuggestionsClassName} style={autoCompleteSuggestionsStyle}>
        {buildSuggestionOptions()}
      </div>
    </div>
  );
};

AutoComplete.propTypes /* remove-proptypes */ = {
  className: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array.isRequired,
  onSelectChoice: PropTypes.func.isRequired,
  initialValue: PropTypes.string,
};

export default AutoComplete;
